import { useMsal } from "@azure/msal-react";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import IconGMD from "../../assets/icons/GMD-48.svg";
import IconHPR from "../../assets/icons/HPR-48.svg";
import IconHoists from "../../assets/icons/Hoists-48.svg";
import SupportIcon from "../../assets/icons/headphones.svg?react";
import { ModuleId } from "../../constants/modules";
import { titles } from "../../constants/titles";
import { ProductionArea } from "../../enums";
import {
  GroupedModules,
  useCurrentProductionArea,
  useModules,
  useSelectedSites,
} from "../../hooks";
import { SiteWithModuleStatusDto } from "../../models/site-with-module-status";
import IncomingCallListener from "../IncomingCall/IncomingCallListener";
import Modal from "../Modal";
import Support from "../Support";
import styles from "./Header.module.css";
import NotificationDropdown from "./NotificationDropdown";
import ServicesDropdown from "./ServicesDropdown";
import SiteSelector from "./SiteSelector";
import UserDropdown from "./UserDropdown";

type HeaderProps = {
  showServiceDropdown: boolean;
  serviceTitle?: string;
  getSiteUrl?: (siteData: SiteWithModuleStatusDto) => string;
  getMultiSiteUrl?: (sites: string[]) => string;
  mode: "service" | "landingPage";
  productionAreaOverride?: ProductionArea;
  unreadNotificationCount?: number;
  /** When false, the site selector offers single-site selection only. Defaults to true. */
  allowMultiSite?: boolean;
};

export default function Header({
  showServiceDropdown,
  serviceTitle,
  getSiteUrl,
  getMultiSiteUrl,
  mode,
  productionAreaOverride,
  unreadNotificationCount = 0,
  allowMultiSite = true,
}: HeaderProps) {
  const { selectedSite, clearSelected, isMultiSiteMode } = useSelectedSites();
  const { search } = useLocation();
  const [supportModalVisible, setSupportModalVisible] = useState(false);

  const { accounts } = useMsal();
  const { modules } = useModules(selectedSite!);

  const homeSearch = (() => {
    if (isMultiSiteMode) return search;
    return "";
  })();

  const handleHomeClick = () => {
    if (!isMultiSiteMode) clearSelected();
  };

  return (
    <>
      <header className={styles.header}>
        <div>
          {mode === "landingPage" ? (
            <Link
              to={{ pathname: "/", search: homeSearch }}
              onClick={handleHomeClick}
            >
              <Branding productionAreaOverride={productionAreaOverride} />
            </Link>
          ) : (
            <a href={`/${homeSearch}`} onClick={handleHomeClick}>
              <Branding productionAreaOverride={productionAreaOverride} />
            </a>
          )}
          {serviceTitle && (
            <h2 className={styles["service-title"]}>{serviceTitle}</h2>
          )}
        </div>
        <div className={styles.controls}>
          {accounts &&
            accounts.length > 0 &&
            !isMultiSiteMode &&
            !!selectedSite && (
              <SupportButton
                modules={modules}
                onClick={() => setSupportModalVisible(true)}
              />
            )}
          {showServiceDropdown && selectedSite && (
            <ServicesDropdown modules={modules} />
          )}
          {!isMultiSiteMode && selectedSite && (
            <NotificationDropdown
              key={selectedSite}
              unreadCount={unreadNotificationCount}
              site={selectedSite}
            />
          )}
          {(mode === "landingPage" || selectedSite) && (
            <SiteSelector
              getSiteUrl={getSiteUrl}
              allowMultiSite={allowMultiSite}
              getMultiSiteUrl={getMultiSiteUrl}
            />
          )}
          {accounts && accounts.length > 0 && <UserDropdown />}
        </div>
      </header>
      {accounts && accounts.length > 0 && <IncomingCallListener />}
      <Modal
        visible={supportModalVisible}
        onDismiss={() => setSupportModalVisible(false)}
      >
        <Support onDismiss={() => setSupportModalVisible(false)} />
      </Modal>
    </>
  );
}

function SupportButton({
  modules,
  onClick,
}: {
  modules: GroupedModules | undefined;
  onClick: () => void;
}) {
  const supportLineEnabled =
    modules !== undefined &&
    Object.values(modules)
      .flat()
      .some((m) => m.id === ModuleId.SupportLine && m.enabled);

  return (
    <button
      type="button"
      className={styles["support-button"]}
      onClick={onClick}
      aria-label="Open support"
      disabled={!supportLineEnabled}
    >
      <SupportIcon className={styles["support-icon"]} />
    </button>
  );
}

function Branding({
  productionAreaOverride,
}: {
  productionAreaOverride?: ProductionArea;
}) {
  const { productionArea } = useCurrentProductionArea();
  const resolvedProductionArea = productionArea ?? productionAreaOverride;
  const brandingIcon =
    resolvedProductionArea === ProductionArea.Grinding
      ? IconGMD
      : resolvedProductionArea === ProductionArea.Hoisting
        ? IconHoists
        : IconHPR;

  return (
    resolvedProductionArea && (
      <div className={styles.branding}>
        <img src={brandingIcon} width={48} height={48}></img>
        <div className={styles.title}>
          <div className={styles.chip}></div>
          <h1>{titles[resolvedProductionArea]} Connect</h1>
        </div>
      </div>
    )
  );
}
